import { useEffect, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { Layout } from './components/Layout';
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { AcceptInvite } from './pages/auth/AcceptInvite';
import { ForgotPassword } from './pages/auth/ForgotPassword';
import { ResetPassword } from './pages/auth/ResetPassword';
import { AcceptTerms } from './pages/auth/AcceptTerms';
import { MfaSetup } from './pages/auth/MfaSetup';
import { EventsList } from './pages/events/EventsList';
import { EventDetail } from './pages/events/EventDetail';
import { EventForm } from './pages/events/EventForm';
import { BulkImportPage } from './pages/events/bulkImport/BulkImportPage';
import { SearchPage } from './pages/events/SearchPage';
import { PublicEventPage } from './pages/events/PublicEventPage';
import { MilestonesListPage } from './pages/events/MilestonesListPage';
import { MilestoneSlideshowSetup } from './pages/events/MilestoneSlideshowSetup';
import { MilestoneContributionsReview } from './pages/events/MilestoneContributionsReview';
import { PublicMilestonePage } from './pages/events/PublicMilestonePage';
import { CollagesListPage } from './pages/collages/CollagesListPage';
import { CollageEditor } from './pages/collages/CollageEditor';
import { CollageViewPage } from './pages/collages/CollageViewPage';
import { PublicCollagePage } from './pages/collages/PublicCollagePage';
import { FacesReviewPage } from './pages/faces/FacesReviewPage';
import { Members } from './pages/account/Members';
import { Settings } from './pages/Settings';
import { CarouselViewer } from './pages/CarouselViewer';
import { About } from './pages/About';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { Help } from './pages/Help';
import { A2HSPrompt } from './components/A2HSPrompt';
import { isNative, hideSplash } from './capacitorInit';

function PrivateRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Hide splash once auth check is done (native only — no-op on web)
  useEffect(() => {
    if (!loading) hideSplash();
  }, [loading]);

  if (loading) return <div className="min-h-screen bg-[#4f46e5]" />;
  // Remember where a logged-out visitor was headed (e.g. an email's
  // /events?tab=shared link) so Login can send them back there.
  if (!user) return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  if (!user.acceptedTermsAt) return <Navigate to="/accept-terms" replace />;
  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading) hideSplash();
  }, [loading]);

  if (loading) return <div className="min-h-screen bg-[#4f46e5]" />;
  if (user) return <Navigate to="/events" replace />;
  return <>{children}</>;
}

function AcceptTermsRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) return <div className="min-h-screen bg-[#4f46e5]" />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.acceptedTermsAt) return <Navigate to="/events" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/share/:token" element={<PublicEventPage />} />
      <Route path="/share/milestone/:token" element={<PublicMilestonePage />} />
      <Route path="/share/collage/:token" element={<PublicCollagePage />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
      <Route path="/accept-terms" element={<AcceptTermsRoute><AcceptTerms /></AcceptTermsRoute>} />
      <Route path="/mfa-setup" element={<MfaSetup />} />
      <Route path="/events" element={<PrivateRoute><EventsList /></PrivateRoute>} />
      <Route path="/events/new" element={<PrivateRoute><EventForm /></PrivateRoute>} />
      <Route path="/events/bulk-import" element={<PrivateRoute><BulkImportPage /></PrivateRoute>} />
      <Route path="/events/milestones" element={<PrivateRoute><MilestonesListPage /></PrivateRoute>} />
      <Route path="/events/milestones/new" element={<PrivateRoute><MilestoneSlideshowSetup /></PrivateRoute>} />
      <Route path="/events/milestones/:id/edit" element={<PrivateRoute><MilestoneSlideshowSetup /></PrivateRoute>} />
      <Route path="/events/milestones/:id/review" element={<PrivateRoute><MilestoneContributionsReview /></PrivateRoute>} />
      <Route path="/events/collages" element={<PrivateRoute><CollagesListPage /></PrivateRoute>} />
      <Route path="/events/collages/new" element={<PrivateRoute><CollageEditor /></PrivateRoute>} />
      <Route path="/events/collages/:id/edit" element={<PrivateRoute><CollageEditor /></PrivateRoute>} />
      <Route path="/events/collages/:id" element={<PrivateRoute><CollageViewPage /></PrivateRoute>} />
      <Route path="/events/:id" element={<PrivateRoute><EventDetail /></PrivateRoute>} />
      <Route path="/events/:id/edit" element={<PrivateRoute><EventForm /></PrivateRoute>} />
      <Route path="/faces" element={<PrivateRoute><FacesReviewPage /></PrivateRoute>} />
      <Route path="/account/members" element={<PrivateRoute><Members /></PrivateRoute>} />
      <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
      <Route path="/search" element={<PrivateRoute><SearchPage /></PrivateRoute>} />
      <Route path="/carousel" element={<PrivateRoute><CarouselViewer /></PrivateRoute>} />
      <Route path="/about" element={<Layout><About /></Layout>} />
      <Route path="/privacy" element={<Layout><Privacy /></Layout>} />
      <Route path="/terms" element={<Layout><Terms /></Layout>} />
      <Route path="/help" element={<PrivateRoute><Help /></PrivateRoute>} />
      <Route path="/" element={<Navigate to="/events" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        {/* A2HS install banner — only shown on the web PWA, never inside the native app */}
        {!isNative && <A2HSPrompt />}
      </AuthProvider>
    </BrowserRouter>
  );
}
